import {
  compose,
  ifElse,
  isNil,
  always,
  join,
  map,
  split,
  uniq,
  replace,
  cond,
  equals,
  gt,
  lt,
  concat,
} from 'ramda';
import dateFormat from 'date-fns/format';
import getMonth from 'date-fns/get_month';
import getYear from 'date-fns/get_year';
import { format } from 'd3-format';

import { Compare, Timespan, Precision } from '../constants';

export const minus = '−';
export const placeholder = minus;
export const dash = minus;

export const withPlaceholder = ifElse(isNil, always(placeholder));

export const intervalCustomFormat = fmt =>
  withPlaceholder(
    compose(
      join(' ' + dash + ' '),
      map(date => dateFormat(date, fmt)),
      split('/'),
    ),
  );
export const collapsibleIntervalCustomFormat = fmt =>
  withPlaceholder(
    compose(
      join(' ' + dash + ' '),
      uniq,
      map(date => dateFormat(date, fmt)),
      split('/'),
    ),
  );

export const timeIntervalFormat = collapsibleIntervalCustomFormat('MMM YYYY');
export const monthRangeFormat = collapsibleIntervalCustomFormat('MMM');
export const yearRangeFormat = collapsibleIntervalCustomFormat('YYYY');

export const timeIntervalPrecisionFormat = {
  [Precision.MONTHLY]: timeIntervalFormat,
  [Precision.YEARLY]: yearRangeFormat,
  [Precision.MONTHLY_AND_YEARLY]: timeIntervalFormat,
};

export const recurringTimeIntervalPrecisionFormat = (precision, interval) => {
  return precision === 'monthly'
    ? recurringMonthFormat(interval)
    : timeIntervalPrecisionFormat[precision](interval);
};

export const recurringMonthFormat = interval => {
  const [start, end] = interval.split('/');
  const monthRange =
    getMonth(start) === getMonth(end)
      ? dateFormat(start, 'MMM')
      : [dateFormat(start, 'MMM'), dateFormat(end, 'MMM')].join('-');

  return `${monthRange} (${getYear(start)}-${getYear(end)})`;
};

export const recurringRangeFormat = (interval, year) => {
  const [start, end] = interval.split('/');
  if (getYear(start) === getYear(end)) {
    return monthRangeFormat(interval) + ' ' + year;
  }
  return [
    dateFormat(start, 'MMM'),
    year,
    dash,
    dateFormat(end, 'MMM'),
    year - getYear(start) + getYear(end),
  ].join(' ');
};

export const valueFormat = withPlaceholder(
  compose(replace('-', minus), format(','), format('.2f')),
);

export const signedValueFormat = cond([
  [isNil, always(placeholder)],
  [equals(0), always('0')],
  [lt(0), compose(concat('+'), format(','), format('.2f'))],
  [gt(0), compose(replace('-', minus), format(','), format('.2f'))],
]);

const timespanDisplay = {
  [Timespan.LAST_MONTH]: 'Last Month',
  [Timespan.LAST_12_MONTHS]: 'Last 12 Month',
  [Timespan.THIS_YEAR]: 'This Year',
  [Timespan.LAST_YEAR]: 'Last Year',
  [Timespan.LAST_5_YEARS]: 'Last 5 Years',
  [Timespan.LAST_10_YEARS]: 'Last 10 Years',
  [Timespan.LAST_20_YEARS]: 'Last 20 Years',
};

export const compareDisplay = {
  [Compare.NONE]: 'None',
  [Compare.PERIOD]: 'Previous Period',
  [Compare.YEAR]: 'Previous Year',
  [Compare.RECURRING]: 'Average',
};

export const getTimespanDisplay = (timespan, timespanInterval) =>
  timespan === Timespan.CUSTOM
    ? timeIntervalFormat(timespanInterval)
    : timespanDisplay[timespan];

export const getCompareDisplay = (compare, compareInterval) =>
  compare === Compare.CUSTOM || compare === Compare.RECURRING
    ? timeIntervalFormat(compareInterval)
    : compareDisplay[compare];

export const getCityNameWithState = (name, state) =>
  name + (state ? ', ' + state.abbreviation : '');



// WEBPACK FOOTER //
// ./src/utils/format.js